/** Mirrors of the C# Vacancies DTO records, field for field. */

/**
 * One vacant plantilla item. Derived on the server from the plantilla — the `id` IS the
 * plantilla item's id. Only the job-posting tag is stored.
 */
export interface VacancyListItem {
  id: string;
  /** The permanent item number, e.g. `PROG-26001-002`. */
  itemNumber: string;
  plantillaPositionId: string;
  /** The post's snapshot title. */
  positionTitle: string;
  /** The post's permanent code — what the plantilla profile is titled by. */
  postCode: string;
  orgUnitId: string;
  orgUnitName: string;
  salaryGrade: number | null;
  /** e.g. "SG 11-3 (Health)". */
  salaryGradeLabel: string | null;
  monthlyAmount: number | null;
  /** The catalog title. Null only on a post that predates the catalog. */
  positionId: string | null;
  /** The catalog title's CURRENT name — the table's group header. The post's snapshot where there is no catalog row. */
  catalogTitle: string;
  /** The catalog title's code. Null with no catalog row. */
  positionCode: string | null;
  /*
    The designation's qualification standards, live from the catalog. The three documents arrive
    as PLAIN TEXT — render them as text, never as markup.
  */
  education: string | null;
  training: string | null;
  experience: string | null;
  eligibilityName: string | null;
  otherExpectations: string | null;
  /** Always true on the list; false on a profile opened after the item was filled. */
  isVacant: boolean;
  /** The item AND its post are live. */
  isActive: boolean;
  isForJobPosting: boolean;
  /**
   * The live posting is PUBLISHED — it takes applicants and Deliberate lists it. Never true without
   * `isForJobPosting`; with it, splits the job-posting facet into three buckets.
   */
  isPublished: boolean;
  /** The live posting is COMPLETED — an applicant endorsed for appointment (Deliberate). Derived. */
  isCompleted: boolean;
  taggedAt: string | null;
  taggedByName: string | null;
  /** The recruitment cycle of the most recent posting. The PROFILE's alone — absent on every list row. */
  cycle?: VacancyCycle | null;
  /** The ITEM's provenance — when the slot was authorized. */
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
}

/**
 * The vacancy's cycle — Job Posting, Initial Screening, Initial Interview — read off its most
 * recent posting, live or ended. Every count is of LIVE applicants.
 */
export interface VacancyCycle {
  /** The posting (the tag) — what applicants attach to. */
  jobPostingTagId: string;
  /** False once the tag was removed or the item filled. */
  isLive: boolean;
  /** Published — it takes applicants and Deliberate lists it. */
  isPublished: boolean;
  /** The INSTANT it was published. Absent on a posting published before the step existed. */
  publishedAt: string | null;
  publishedByName: string | null;
  postedByUserId: string | null;
  postedByName: string | null;
  /** A calendar date (`YYYY-MM-DD`) — `formatDateOnly`, never the instant path. */
  postedDate: string | null;
  remarks: string | null;
  applicantCount: number;
  screenedCount: number;
  qualifiedCount: number;
  disqualifiedCount: number;
  interviewScheduledCount: number;
}

/** The Job Posting stage's three values — the tag form and its edit. */
export interface JobPostingTagRequest {
  postedByUserId: string;
  postedDate: string;
  remarks: string | null;
}

/** A live account for the "Posted by" and "Screened by" pickers. */
export interface RecruitmentUserOption {
  userId: string;
  displayName: string;
}

/**
 * What the module writes, for its History Logs — the one list both the index and any other
 * surface read. A RENAME keeps the old CLR name here forever; the trail is append-only.
 */
export const VACANCY_AUDIT_ENTITY_TYPES = ['JobPostingTag'];

/** One vacant, untagged item for the tag form — grouped under its catalog title. */
export interface VacancyOption {
  id: string;
  itemNumber: string;
  /** Null only on a post that predates the catalog; such an item groups under its POST instead. */
  positionId: string | null;
  positionTitle: string;
  positionCode: string | null;
  plantillaPositionId: string;
  /** The unit — the item's Office Assignment. */
  orgUnitName: string;
}

/**
 * The key the tag form groups an item under: its catalog title, or — for a post that predates
 * the catalog — its post, so two legacy posts that share a snapshot title are never merged.
 */
export function vacancyGroupKey(option: VacancyOption): string {
  return option.positionId != null ? `position:${option.positionId}` : `post:${option.plantillaPositionId}`;
}
